import { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

interface UploadResult {
  downloadUrl: string;
  s3Key: string;
  metadata: {
    fileName: string;
    fileSize: number;
    recordCount: number;
    dateUploaded: string;
  };
}

interface UseTaxListUploadOptions {
  endpoint: string; // e.g., '/admin/dfpb-orders/{orderId}/upload-dfpb-list'
  onSuccess?: (result: UploadResult) => void;
  onError?: (error: Error) => void;
}

export function useTaxListUpload({ endpoint, onSuccess, onError }: UseTaxListUploadOptions) {
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file: File) => {
    try {
      setUploading(true);

      // Get auth token
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token');
      }

      const apiUrl = import.meta.env.VITE_API_URL;

      // Create FormData with file
      const formData = new FormData();
      formData.append('file', file);

      // Upload file directly to backend
      const uploadResponse = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }

      const result: UploadResult = await uploadResponse.json();

      // Call success callback
      onSuccess?.(result);

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to upload file');
      console.error('Error uploading file:', error);
      onError?.(error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploading,
    uploadFile
  };
}
